'use client';

import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import type { HeroCardSearchResolved } from './types';
import { Button } from '@/components/Button';
import { ZipCity, type ZipCityLocation } from '@/components/ZipCity';
import { pushCmsEventTracking } from '@/lib/gtm/pushCmsEventTracking';
import { useRenderContext } from '@/lib/render/RenderContextProvider';
import { useLocationFinder } from '@/lib/useLocationFinder';

/**
 * `stacked` puts the button under the input, which is what the narrow slot
 * cards need. `inline` sets them side by side from `md` up, for the
 * full-width centered card where a full-width button would stretch absurdly.
 */
export type HeroCardSearchLayout = 'stacked' | 'inline';

interface HeroCardSearchProps {
  search: HeroCardSearchResolved;
  layout?: HeroCardSearchLayout;
}

/** Where the submit points when the card configures no target of its own. */
const DEFAULT_SEARCH_HREF = '/standorte/suche/';

/**
 * Turns the configured target into a root-relative href. Editors type it
 * without a leading slash (`standorte/suche`), and an absolute URL or an
 * already-rooted path is left alone.
 */
export function searchHref(target: string): string {
  const value = target.trim();
  if (!value) {
    return DEFAULT_SEARCH_HREF;
  }
  if (/^(https?:)?\/\//i.test(value) || value.startsWith('/')) {
    return value;
  }
  return `/${value}`;
}

/**
 * The PLZ/Ort finder shared by the Prices card and the Home card's tab 1.
 *
 * The submit is an anchor rather than a button, so the search page is
 * reachable by a crawler — the legacy site does the same
 * (`<a class="…_form_submit" href="/standorte/suche/">`). Clicking it still
 * runs the JS search: the handler cancels the navigation and lets
 * `useLocationFinder` decide, so a query matching one Standort goes straight
 * there instead of to the results page.
 */
export function HeroCardSearch({
  search,
  layout = 'stacked',
}: HeroCardSearchProps) {
  const inline = layout === 'inline';
  const { substitutePlaceholders: sub, entities } = useRenderContext();
  // On the search results page the input opens with the term the visitor
  // searched for, so they can refine it instead of retyping. The entity is set
  // only there (10.02.11), so every other page opens empty.
  const seededTerm = entities.locationSearch?.term;
  const { query, handleValueChange, handleSelect, handleSubmit } =
    useLocationFinder(undefined, seededTerm);

  const placeholder = search.placeholder ? sub(search.placeholder) : undefined;
  const buttonLabel = sub(search.buttonLabel);
  const href = searchHref(sub(search.anchor));

  const submit = (q: string, results: ZipCityLocation[]) => {
    pushCmsEventTracking(search.tracking, { label: buttonLabel });
    handleSubmit(q, results);
  };

  // Picking a suggestion — by click or by Enter on the highlighted row — is the
  // same completed search as a submit that resolves to a single Standort, and
  // lands on the same page, so it has to push the same event. Passing
  // `handleSelect` straight through navigated without tracking, leaving the
  // dropdown route uncounted.
  const select = (loc: ZipCityLocation) => {
    pushCmsEventTracking(search.tracking, { label: buttonLabel });
    handleSelect(loc);
  };

  return (
    <div
      className={classNames(
        'flex w-full flex-col gap-3',
        inline && 'md:flex-row md:items-center md:justify-center',
      )}
    >
      {/* ZipCity anchors its dropdown to its own `relative` root, so the
          wrapper only carries width. */}
      <div className={classNames('w-full', inline && 'md:max-w-sm')}>
        <ZipCity
          value={query}
          onValueChange={handleValueChange}
          onSelect={select}
          onSubmit={submit}
          placeholder={placeholder}
          seededQuery={seededTerm}
          inputClassName="h-[50px] w-full rounded-full bg-[#eae8e7] px-5 text-base outline-none placeholder:text-foreground/50 focus:ring-2 focus:ring-primary/20"
        />
      </div>
      <Button
        href={href}
        variant="primary"
        size="md"
        onClick={(event) => {
          // The href is what a crawler follows; a real click defers to the
          // finder, which may land on a Standort page instead.
          event.preventDefault();
          submit(query, []);
        }}
        className={classNames(
          'w-full min-w-0 px-4! text-base',
          inline && 'text-lg md:w-auto md:shrink-0 md:px-12!',
        )}
      >
        {buttonLabel}
      </Button>
    </div>
  );
}
